/**
 * @file paymentSessionStorage.ts
 * @description localStorage session management for TournamentJoinPayment (Task 27.1)
 * 
 * PRD Reference: §6.1 Target Schema, §6.2 Lifecycle & Recovery, §6.3 Backward Compatibility
 */

import {
  PAYMENT_SESSION_STORAGE_KEY_PREFIX,
  PAYMENT_SESSION_SCHEMA_VERSION,
  PAYMENT_SESSION_EXPIRY_MS,
} from './constants';

// Re-export constants for backward compatibility
export {
  PAYMENT_SESSION_STORAGE_KEY_PREFIX,
  PAYMENT_SESSION_SCHEMA_VERSION,
  PAYMENT_SESSION_EXPIRY_MS,
};

/**
 * Extended session schema (v3) with userId for user-scoped storage (Task 37.2)
 * PRD Reference: §6.1 Target Schema
 */
export interface PaymentSessionSchema {
  /** Payment ID from backend */
  paymentId: string;
  /** Tournament ID this payment is for */
  tournamentId: string;
  /** User ID who owns this session (Task 37.2) */
  userId?: string;
  /** Unix timestamp (ms) when session was created */
  createdAt: number;
  /** Unix timestamp (ms) when session expires - from payment.expiresAt */
  expiresAt: number;
  /** Payment amount (e.g., "50.00") */
  amount: string;
  /** Payment currency (e.g., "POL") */
  currency: string;
  /** Schema version for migration (3 = with userId) */
  schemaVersion: 2 | 3;
}

/** Legacy v1 schema for migration */
interface LegacySessionV1 {
  paymentId: string;
  tournamentId: string;
  createdAt: number;
}

interface SetPaymentSessionOptions {
  /** Force overwrite even if valid session exists */
  force?: boolean;
}

/**
 * Get user-scoped storage key for a tournament (Task 37.1)
 * Format: {prefix}_{userId}_{tournamentId}
 * 
 * @param userId - User ID (required, must be non-empty)
 * @param tournamentId - Tournament ID
 * @returns User-scoped localStorage key
 * @throws Error if userId is falsy
 */
export const getStorageKey = (userId: string, tournamentId: string): string => {
  if (!userId) {
    throw new Error('userId is required for getStorageKey');
  }
  return `${PAYMENT_SESSION_STORAGE_KEY_PREFIX}_${userId}_${tournamentId}`;
};

/**
 * Check if a localStorage key is in legacy format (without userId)
 * Legacy format: {prefix}_{tournamentId} (no userId segment)
 * New format: {prefix}_{userId}_{tournamentId}
 * 
 * @param key - localStorage key to check
 * @returns true if key is legacy format, false otherwise
 */
export const isLegacyKey = (key: string): boolean => {
  if (!key.startsWith(PAYMENT_SESSION_STORAGE_KEY_PREFIX + '_')) {
    return false;
  }
  // Extract suffix after prefix + underscore
  const suffix = key.slice(PAYMENT_SESSION_STORAGE_KEY_PREFIX.length + 1);
  // Legacy format has no underscore in suffix (just tournamentId)
  // New format has underscore: userId_tournamentId
  // But tournamentId itself may contain underscores (e.g., "tour_123")
  // Legacy: "tour_123" -> parts = ["tour", "123"] (2 parts from tournamentId)
  // New: "user_1_tour_123" -> parts = ["user", "1", "tour", "123"] (4+ parts)
  // Heuristic: legacy has exactly 2 parts (assuming tournamentId format is "tour_XXX")
  const parts = suffix.split('_');
  return parts.length <= 2;
};

/**
 * Check if a session is expired based on expiresAt
 */
const isSessionExpired = (session: PaymentSessionSchema): boolean => {
  return Date.now() > session.expiresAt;
};

/**
 * Migrate v1 session to v2 schema
 * PRD Reference: §6.3 Backward Compatibility & Migration
 */
const migrateV1ToV2 = (v1Session: LegacySessionV1): PaymentSessionSchema | null => {
  // Calculate expiresAt from createdAt + 15 minutes
  const expiresAt = v1Session.createdAt + PAYMENT_SESSION_EXPIRY_MS;

  // If already expired, return null
  if (Date.now() > expiresAt) {
    return null;
  }

  return {
    paymentId: v1Session.paymentId,
    tournamentId: v1Session.tournamentId,
    createdAt: v1Session.createdAt,
    expiresAt,
    amount: '', // Fallback for missing field
    currency: '', // Fallback for missing field
    schemaVersion: 2,
  };
};

/**
 * Validate that parsed data has required fields
 */
const isValidSession = (data: unknown): data is PaymentSessionSchema | LegacySessionV1 => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.paymentId === 'string' &&
    typeof obj.tournamentId === 'string' &&
    typeof obj.createdAt === 'number'
  );
};

/**
 * Get payment session from localStorage (Task 37.1 - user-scoped)
 * Handles migration from v1 to v2 schema and expiry validation
 * 
 * PRD Reference: §6.2 Lifecycle & Recovery
 * 
 * @param userId - User ID (required for user-scoped key)
 * @param tournamentId - Tournament ID to get session for
 * @returns Session data or null if not found/expired/invalid
 */
export const getPaymentSession = (userId: string, tournamentId: string): PaymentSessionSchema | null => {
  try {
    const key = getStorageKey(userId, tournamentId);
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Validate required fields
    if (!isValidSession(parsed)) {
      // Invalid data, clear it
      localStorage.removeItem(key);
      return null;
    }

    // Task 37.2: Validate userId matches (security invariant INV-37.2)
    const storedUserId = (parsed as { userId?: string }).userId;
    if (storedUserId && storedUserId !== userId) {
      console.warn(
        `[paymentSessionStorage] userId mismatch: expected ${userId}, got ${storedUserId}. Removing invalid session.`
      );
      localStorage.removeItem(key);
      return null;
    }

    // Check if v1 schema (no schemaVersion or version < 2)
    const schemaVersion = (parsed as { schemaVersion?: number }).schemaVersion;
    if (!schemaVersion || schemaVersion < 2) {
      const migrated = migrateV1ToV2(parsed as LegacySessionV1);
      if (!migrated) {
        // Expired during migration
        localStorage.removeItem(key);
        return null;
      }
      return migrated;
    }

    const session = parsed as PaymentSessionSchema;

    // Check expiry for v2/v3 schema
    if (isSessionExpired(session)) {
      localStorage.removeItem(key);
      return null;
    }

    return session;
  } catch {
    // JSON parse error or other issues
    return null;
  }
};

/**
 * Save payment session to localStorage (Task 37.1 - user-scoped)
 * Includes duplicate session guard to prevent overwriting valid sessions
 * 
 * PRD Reference: §6.2 Lifecycle & Recovery
 * 
 * @param userId - User ID (required for user-scoped key)
 * @param tournamentId - Tournament ID to save session for
 * @param session - Session data to save
 * @param options - Options including force flag
 * @returns true if saved, false if blocked by duplicate guard
 */
export const setPaymentSession = (
  userId: string,
  tournamentId: string,
  session: PaymentSessionSchema,
  options: SetPaymentSessionOptions = {}
): boolean => {
  try {
    // Duplicate session guard: check if valid session already exists
    if (!options.force) {
      const existing = getPaymentSession(userId, tournamentId);
      if (existing) {
        console.warn(
          `[paymentSessionStorage] Blocked duplicate session for tournament ${tournamentId}. ` +
          `Existing paymentId: ${existing.paymentId}`
        );
        return false;
      }
    }

    const key = getStorageKey(userId, tournamentId);
    // Include userId in stored data and set schemaVersion to 3 (Task 37.2)
    const sessionWithUser: PaymentSessionSchema = {
      ...session,
      userId,
      schemaVersion: 3,
    };
    localStorage.setItem(key, JSON.stringify(sessionWithUser));
    return true;
  } catch (error) {
    console.error('[paymentSessionStorage] Failed to save session:', error);
    return false;
  }
};

/**
 * Clear payment session from localStorage (Task 37.1 - user-scoped)
 * Idempotent - safe to call multiple times
 * 
 * PRD Reference: §6.2 Lifecycle & Recovery
 * 
 * @param userId - User ID (required for user-scoped key)
 * @param tournamentId - Tournament ID to clear session for
 */
export const clearPaymentSession = (userId: string, tournamentId: string): void => {
  try {
    const key = getStorageKey(userId, tournamentId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[paymentSessionStorage] Failed to clear session:', error);
  }
};

/**
 * Check if a valid (unexpired) session exists for a tournament (Task 37.1 - user-scoped)
 * 
 * @param userId - User ID (required for user-scoped key)
 * @param tournamentId - Tournament ID to check
 * @returns true if valid session exists
 */
export const hasExistingSession = (userId: string, tournamentId: string): boolean => {
  return getPaymentSession(userId, tournamentId) !== null;
};

/**
 * Clear all legacy (non-user-scoped) payment sessions from localStorage (Task 37.3)
 * Legacy keys have format: prefix_tournamentId (no userId segment)
 * New keys have format: prefix_userId_tournamentId
 * 
 * @returns Number of legacy sessions removed
 */
export const clearLegacySessions = (): number => {
  try {
    const keysToRemove: string[] = [];
    
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && isLegacyKey(key)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove legacy keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    if (keysToRemove.length > 0) {
      console.info(
        `[paymentSessionStorage] Cleared ${keysToRemove.length} legacy session(s): ${keysToRemove.join(', ')}`
      );
    }
    
    return keysToRemove.length;
  } catch (error) {
    console.error('[paymentSessionStorage] Failed to clear legacy sessions:', error);
    return 0;
  }
};

/**
 * Create a new session schema from payment response
 * Helper for hook to create properly typed session
 * 
 * @param tournamentId - Tournament ID
 * @param paymentId - Payment ID from backend
 * @param expiresAt - Expiry timestamp (ISO string or ms)
 * @param amount - Payment amount
 * @param currency - Payment currency
 * @returns Properly typed session schema
 */
export const createSessionSchema = (
  tournamentId: string,
  paymentId: string,
  expiresAt: string | number,
  amount: string,
  currency: string
): PaymentSessionSchema => {
  const expiresAtMs = typeof expiresAt === 'string' 
    ? new Date(expiresAt).getTime() 
    : expiresAt;

  return {
    paymentId,
    tournamentId,
    createdAt: Date.now(),
    expiresAt: expiresAtMs,
    amount,
    currency,
    schemaVersion: 2,
  };
};
